import CustomLoader from "@/components/CustomLoader/customLoader.component";
import { fetchTags } from "@/models/tags.model";
import {
  advancedSearchTasks,
  basicSearchTasks,
  fetchFavoriteSearches,
  fetchTask,
  getUserTasks,
  sendUpdatedSearchFavorites,
} from "@/models/tasks.model";
import {
  fetchAllTimers,
  fetchMyTimers,
  fetchUserTimers,
  sendNewUserTimer,
  sendStartTimer,
  sendStopTimer,
  updateTimer,
} from "@/models/timers.model";
import { fetchAllUsers } from "@/models/users.model";
import { AppContext, defaultState } from "@/services/AppContext/appContext";
import {
  BasicTask,
  Collaborator,
  FavoriteSearch,
  FetchTaskParams,
  FullTask,
  GenericProviderState,
  OrganizedTimersEvent,
  OrganizedTimersEvents,
  SearchTask,
  SearchTaskBasic,
  ShortAppUser,
  TagCategory,
  UserTimer,
} from "@/types";
import {
  checkLock,
  createLock,
  locForClearFavoriteSearches,
  locForGetAllUsers,
  locForGetFavoriteSearches,
  locForGetPreCacheTasks,
  locForGetUserTags,
  locForGetUserTasks,
  locForGetUserTimers,
  locForSetAllUsers,
  locForSetFavoriteSearches,
  locForSetPreCacheTasks,
  locForSetUserTags,
  locForSetUserTasks,
  locForSetUserTimers,
  removeLock,
} from "@/utils/localForageUtils";
import { getTokensLocalStorage } from "@/utils/tokenHandler.util";
import {
  genericFailureToast,
  genericSuccessToast,
  getUserTokenID,
  organizeTimersByDate,
} from "@/utils/utils";
import localforage from "localforage";
import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

// Define a type for basic search tasks with timestamp
export type SearchTaskBasicWithTimestamp = {
  tasks: SearchTaskBasic[];
  timestamp: number;
};

interface AppProviderProps {
  children: ReactNode;
}

export type TimedTask = {
  taskID: number;
  taskName: string;
  taskStartDate: Date;
};

const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { taskID } = useParams();
  const [user, setUser] = useState<ShortAppUser | null>(null);
  const [allAppUsers, setAllAppUsers] = useState<ShortAppUser[] | null>(null);
  const [runningTimer, setRunningTimer] = useState<UserTimer | null>(
    defaultState.runningTimer
  );
  const [timerLock, setTimerLock] = useState<string | null>(null);
  const [userTasks, setUserTasks] = useState<BasicTask[] | null>(null);
  const [userTags, setUserTags] = useState<TagCategory[] | null>(null);
  const [userTimers, setUserTimers] = useState<UserTimer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pageTimers, setPageTimers] = useState<
    OrganizedTimersEvents | undefined
  >(undefined);
  // TODO: specify this is for timers
  const [fetchEveryoneTimers, setFetchEveryoneTimers] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isLockedAnimation, setIsLockedAnimation] = useState(false);

  const controllerRef = useRef(new AbortController());

  // Track the current controllers
  const getUserTasksControllerRef = useRef<AbortController | null>(null);
  const getUserTimersControllerRef = useRef<AbortController | null>(null);
  const getUserFavoriteSearchesControllerRef = useRef<AbortController | null>(
    null
  );
  const getAllAppUsersControllerRef = useRef<AbortController | null>(null);
  const getUserTagsControllerRef = useRef<AbortController | null>(null);
  const getPrecacheTasksControllerRef = useRef<AbortController | null>(null);

  // Collaborators
  const [collaborators, setCollaborators] = useState<Collaborator[] | null>(
    null
  );

  // Pre-cache (searching) - now stores only basic tasks
  const [precacheTasks, setPrecacheTasks] = useState<SearchTaskBasic[] | null>(
    null
  );

  // Search favorites
  const [favoriteSearches, setFavoriteSearches] = useState<FavoriteSearch[]>(
    []
  );

  // Single Task (Side View)
  const [singleTask, setSingleTask] = React.useState<
    GenericProviderState<FullTask>
  >({
    data: null,
    loading: true,
    error: null,
  });
  // Update the active task ID whenever taskID prop changes
  const activeTaskIdRef = useRef<number | undefined>(Number(taskID));

  useLayoutEffect(() => {
    console.log("FIRST USE EFFECT");
    async function fetchUserDetails() {
      if (!allAppUsers || allAppUsers.length === 0) {
        return;
      }

      const { refreshToken } = await getTokensLocalStorage();

      const userID = getUserTokenID(refreshToken || "");
      console.log("User ID", userID);

      if (userID === null) {
        console.error("User ID not found");
        return;
      }

      // find user details
      const foundUser = allAppUsers.find((user) => user.id === userID);
      if (!foundUser) {
        console.error("User not found");
        return;
      }
      setUser(foundUser);
      getAllCollaborators(allAppUsers);
    }

    fetchUserDetails();
  }, [allAppUsers]);

  const refetchTags = useCallback(async () => {
    // Abort any existing request before starting a new one
    if (getUserTagsControllerRef.current) {
      getUserTagsControllerRef.current.abort();
    }

    // Create a new controller for this request
    const controller = new AbortController();
    getUserTagsControllerRef.current = controller;

    const response = await fetchTags({ signal: controller.signal });
    if (response.rStatus === "error") {
      if (response.message === "request_aborted" || controller.signal.aborted) {
        console.log("Request aborted, skipping tags refetch");
        return;
      }
      console.error("Failed to fetch user tags");
      setError("Failed to fetch user tags");
      return;
    }
    console.log("New tags", response.tags);
    setUserTags(response.tags);
    await locForSetUserTags(response.tags);

    // Clear the controller reference when the request completes
    if (getUserTagsControllerRef.current === controller) {
      getUserTagsControllerRef.current = null;
    }
  }, []);

  // // Whenever userTasks changes, check tags in userTasks against userTags, refetch userTags if different
  useLayoutEffect(() => {
    if (!userTasks || !userTags) {
      return;
    }

    // User's task tags
    const userTaskTagsIDs = userTasks.flatMap((task) =>
      task.tags.map((tag) => tag.id)
    );

    // User's tags
    const userTagIDs = userTags.flatMap((tagCategory) =>
      tagCategory.tags.map((tag) => tag.id)
    );

    // Missing tags
    const missingTags = userTaskTagsIDs.filter(
      (id) => !userTagIDs.includes(id)
    );

    // If any are missing, ask user if they want to fetch them
    if (missingTags.length > 0) {
      // Dismiss any existing toast with the same message
      toast.dismiss("new-tags-toast");
      console.log("Setting should fetch tags");
      refetchTags();
      // toast.info("You have new tags, would you like to fetch them?", {
      //   id: "new-tags-toast",
      //   duration: Infinity,
      //   action: (
      //     <div className="flex flex-col justify-center gap-1">
      //       <Button
      //         variant="outline"
      //         size="sm"
      //         onClick={() => {
      //           refetchTags();
      //           toast.dismiss("new-tags-toast");
      //         }}
      //       >
      //         Fetch
      //       </Button>
      //       <Button
      //         variant="outline"
      //         size="sm"
      //         onClick={() => toast.dismiss("new-tags-toast")}
      //       >
      //         Cancel
      //       </Button>
      //     </div>
      //   ),
      // });
    }
  }, [refetchTags, userTags, userTasks]);

  const initialGetAllAppUsers = useCallback(async (force?: boolean) => {
    // Abort any existing request before starting a new one
    if (getAllAppUsersControllerRef.current) {
      getAllAppUsersControllerRef.current.abort();
    }

    // Create a new controller for this request
    const controller = new AbortController();
    getAllAppUsersControllerRef.current = controller;

    const lsAllAppUsers = await locForGetAllUsers();
    if (force || !lsAllAppUsers) {
      const response = await fetchAllUsers({ signal: controller.signal });
      if (response.rStatus === "error") {
        if (
          response.message === "request_aborted" ||
          controller.signal.aborted
        ) {
          console.log("Request aborted, skipping all users fetch");
          return;
        }

        console.error("Failed to fetch all users");
        setError("Failed to fetch all users");
        return;
      }
      setAllAppUsers(response.data);
      await locForSetAllUsers(response.data);
    } else {
      setAllAppUsers(lsAllAppUsers);
    }

    // Clear the controller reference when the request completes
    if (getAllAppUsersControllerRef.current === controller) {
      getAllAppUsersControllerRef.current = null;
    }
  }, []);

  const initialGetUserTasks = useCallback(
    async ({ signal, force }: { signal?: AbortSignal; force?: boolean }) => {
      // Abort any existing request before starting a new one
      if (getUserTasksControllerRef.current) {
        getUserTasksControllerRef.current.abort();
      }

      // Create a new controller for this request
      const controller = new AbortController();
      getUserTasksControllerRef.current = controller;

      // Use the provided signal or the new controller's signal
      const requestSignal = signal || controller.signal;

      const lsUserTasks = await locForGetUserTasks();
      if (force || !lsUserTasks) {
        const response = await getUserTasks({ signal: requestSignal });
        console.log("response: ", response);
        if (response.rStatus === "error") {
          if (response.message === "request_aborted" || requestSignal.aborted) {
            console.log("Request aborted, skipping user tasks fetch");
            return;
          }
          console.error("Failed to fetch user tasks");
          setError("Failed to fetch user tasks");
          setUserTasks([]);
          // Toast error w/ refetch
          return;
        }
        const deepCloneTasks = cloneDeep(response.tasks);
        const deepCloneUserTasks = cloneDeep(userTasks);
        if (!isEqual(deepCloneTasks, deepCloneUserTasks)) {
          console.log("different tasks setting user tasks");
          setUserTasks(response.tasks);
          await locForSetUserTasks(response.tasks);
        } else {
          console.log("same tasks, not setting user tasks");
        }
      } else {
        const deepCloneTasks = cloneDeep(lsUserTasks);
        const deepCloneUserTasks = cloneDeep(userTasks);
        if (!isEqual(deepCloneTasks, deepCloneUserTasks)) {
          console.log("different tasks setting user tasks");
          setUserTasks(lsUserTasks);
        }
      }

      // Clear the controller reference when the request completes
      if (getUserTasksControllerRef.current === controller) {
        getUserTasksControllerRef.current = null;
      }
    },
    [userTasks]
  );

  const initialGetUserTags = useCallback(
    async (force?: boolean) => {
      // Abort any existing request before starting a new one
      if (getUserTagsControllerRef.current) {
        getUserTagsControllerRef.current.abort();
      }

      // Create a new controller for this request
      const controller = new AbortController();
      getUserTagsControllerRef.current = controller;

      const lsUserTags = await locForGetUserTags();
      if (force || !lsUserTags) {
        const response = await fetchTags({ signal: controller.signal });
        console.log("response: ", response);
        if (response.rStatus === "error") {
          if (
            response.message === "request_aborted" ||
            controller.signal.aborted
          ) {
            console.log("Request aborted, skipping user tags fetch");
            return;
          }
          console.error("Failed to fetch user tags");
          setError("Failed to fetch user tags");
          return;
        }
        console.log("Setting user tags");
        const deepCloneTags = cloneDeep(response.tags);
        const deepCloneUserTags = cloneDeep(userTags);
        if (!isEqual(deepCloneTags, deepCloneUserTags)) {
          console.log("different tags setting user tags");
          setUserTags(response.tags);
          await locForSetUserTags(response.tags);
        }
      } else {
        const deepCloneTags = cloneDeep(lsUserTags);
        const deepCloneUserTags = cloneDeep(userTags);
        if (!isEqual(deepCloneTags, deepCloneUserTags)) {
          setUserTags(lsUserTags);
        }
      }

      // Clear the controller reference when the request completes
      if (getUserTagsControllerRef.current === controller) {
        getUserTagsControllerRef.current = null;
      }
    },
    [userTags]
  );

  const initialGetUserTimers = useCallback(
    async (userID: number, force: boolean) => {
      // Abort any existing request before starting a new one
      if (getUserTimersControllerRef.current) {
        getUserTimersControllerRef.current.abort();
      }

      // Create a new controller for this request
      const controller = new AbortController();
      getUserTimersControllerRef.current = controller;

      const lsUserTimers = await locForGetUserTimers();
      if (!force && lsUserTimers) {
        const deepCloneTimers = cloneDeep(lsUserTimers);
        const deepCloneUserTimers = cloneDeep(userTimers);
        if (!isEqual(deepCloneTimers, deepCloneUserTimers)) {
          setUserTimers(lsUserTimers);
        }
        // Clear the controller reference when the request completes
        if (getUserTimersControllerRef.current === controller) {
          getUserTimersControllerRef.current = null;
        }
        return;
      }

      const response = await fetchUserTimers({
        userID,
        signal: controller.signal,
      });
      console.log("response: ", response);
      if (response.rStatus === "error") {
        if (
          response.message === "request_aborted" ||
          controller.signal.aborted
        ) {
          console.log("Request aborted, skipping user timers fetch");
          return;
        }
        console.error("Failed to fetch user timers");
        setError("Failed to fetch user timers");
        return;
      }

      const deepCloneTimers = cloneDeep(response.timers);
      const deepCloneUserTimers = cloneDeep(userTimers);
      if (!isEqual(deepCloneTimers, deepCloneUserTimers)) {
        setUserTimers(response.timers);
        await locForSetUserTimers(response.timers);
      }

      // Clear the controller reference when the request completes
      if (getUserTimersControllerRef.current === controller) {
        getUserTimersControllerRef.current = null;
      }
    },
    [userTimers]
  );

  // Timers on Timers page.
  const fetchPageTimers = useCallback(
    async (signal: AbortSignal, fetchAll: boolean) => {
      if (!user) {
        console.error("User not found");
        return;
      }
      setPageLoading(true);
      console.log(`Fetching ${fetchAll ? "all" : "my"} timers...`);
      const fetchFunction = fetchAll ? fetchAllTimers : fetchMyTimers;
      const result = await fetchFunction({ signal });

      if (signal.aborted) {
        return;
      }

      if (result.rStatus === "success") {
        const organizedTimers = organizeTimersByDate(result.timers);
        setPageTimers(organizedTimers);
      } else {
        // Handle error
        console.error("Failed to fetch timers", result);
        genericFailureToast("Failed to fetch timers");
      }
      setPageLoading(false);
    },
    [user]
  );

  useLayoutEffect(() => {
    if (!user) {
      return;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;

    fetchPageTimers(signal, fetchEveryoneTimers);

    return () => {
      controllerRef.current.abort();
    };
  }, [fetchPageTimers, fetchEveryoneTimers, user]);

  const toggleFetchAll = () => {
    console.log("Toggling fetch all timers");
    setFetchEveryoneTimers((prev) => {
      const newFetchAll = !prev;
      fetchPageTimers(controllerRef.current.signal, newFetchAll);
      return newFetchAll;
    });
  };

  const initialGetUserFavoriteSearches = useCallback(
    async ({ signal, force }: { signal?: AbortSignal; force?: boolean }) => {
      // Abort any existing request before starting a new one
      if (getUserFavoriteSearchesControllerRef.current) {
        getUserFavoriteSearchesControllerRef.current.abort();
      }

      // Create a new controller for this request
      const controller = new AbortController();
      getUserFavoriteSearchesControllerRef.current = controller;
      // Use the provided signal or the new controller's signal
      const requestSignal = signal || controller.signal;

      const lsFavoriteSearches = await locForGetFavoriteSearches();
      if (force || !lsFavoriteSearches) {
        const response = await fetchFavoriteSearches({ signal: requestSignal });
        console.log("response: ", response);
        if (response.rStatus === "error") {
          if (response.message === "request_aborted" || requestSignal.aborted) {
            console.log(
              "Request aborted, skipping user favorite searches fetch"
            );
            return;
          }
          console.error("Failed to fetch user favorite searches");
          setError("Failed to fetch user favorite searches");
          return;
        }
        setFavoriteSearches(response.history);
        await locForSetFavoriteSearches(response.history);
      } else {
        setFavoriteSearches(lsFavoriteSearches);
      }

      // Clear the controller reference when the request completes
      if (getUserFavoriteSearchesControllerRef.current === controller) {
        getUserFavoriteSearchesControllerRef.current = null;
      }
    },
    []
  );

  function removeTaskFromUserTasks(taskID: number) {
    if (!userTasks) {
      return;
    }

    const newTasks = userTasks.filter((task) => task.id !== taskID);
    setUserTasks(newTasks);
    locForSetUserTasks(newTasks);
  }

  // Function to put together all collaborators
  function getAllCollaborators(ShortAppUser: ShortAppUser[]) {
    const allCollaborators: Collaborator[] = [];
    ShortAppUser.forEach((user) => {
      allCollaborators.push({
        id: user.id,
        email: user.email,
        full_name: `${user.first_name} ${user.surname}`,
      });
    });
    setCollaborators(allCollaborators);
  }

  // TODO: all being 6 months, persist this setting, preferences in the future?
  const getPrecacheTasks = useCallback(
    async (all: boolean, force: boolean, showToastSuccess?: boolean) => {
      console.log("<----- HIT getPrecacheTasks ----->");
      console.log("all", all);
      console.log("force", force);
      console.log("showToastSuccess", showToastSuccess);

      // Abort any existing request before starting a new one
      if (getPrecacheTasksControllerRef.current) {
        getPrecacheTasksControllerRef.current.abort();
      }

      // Create a new controller for this request
      const controller = new AbortController();
      getPrecacheTasksControllerRef.current = controller;

      const precacheData = await locForGetPreCacheTasks();
      const currentTime = new Date().getTime();

      // If there is cached data and force is not true
      if (precacheData && !force) {
        // If less than 5 minutes, use the cached data
        if (currentTime - precacheData.timestamp < 5 * 60 * 1000) {
          // only set if there is a difference in the tasks
          const deepCloneTask = cloneDeep(precacheData.tasks);
          const deepClonePrecache = cloneDeep(precacheTasks);
          if (!isEqual(deepCloneTask, deepClonePrecache)) {
            console.log("different tasks setting precache tasks");
            setPrecacheTasks(precacheData.tasks);
          }
          // Clear the controller reference when the request completes
          if (getPrecacheTasksControllerRef.current === controller) {
            getPrecacheTasksControllerRef.current = null;
          }
          return;
        }
      }

      try {
        const response = await basicSearchTasks({
          all: all,
          signal: controller.signal,
        });
        console.log(response);
        if (response.rStatus === "error") {
          if (
            response.message === "request_aborted" ||
            controller.signal.aborted
          ) {
            console.log("Request aborted, skipping precache tasks fetch");
            return;
          }
          console.error(response.message);
          genericFailureToast("Failed to fetch precache");
          return;
        }

        const newPrecacheData: SearchTaskBasicWithTimestamp = {
          tasks: response.tasks,
          timestamp: currentTime,
        };

        await locForSetPreCacheTasks(newPrecacheData);
        // only set if there is a difference in the tasks
        const deepCloneTask = cloneDeep(response.tasks);
        const deepClonePrecache = cloneDeep(precacheTasks);
        if (!isEqual(deepCloneTask, deepClonePrecache)) {
          console.log("different tasks setting precache tasks");
          setPrecacheTasks(response.tasks);
        } else {
          console.log("same tasks, not setting precache tasks");
        }

        if (showToastSuccess) {
          genericSuccessToast("Precache fetched");
        }
      } catch (error) {
        console.log("Error fetching tasks:", error);
        setError("Error fetching tasks");
        genericFailureToast("Failed to fetch precache");
      }

      // Clear the controller reference when the request completes
      if (getPrecacheTasksControllerRef.current === controller) {
        getPrecacheTasksControllerRef.current = null;
      }
    },
    [precacheTasks]
  );

  useLayoutEffect(() => {
    getPrecacheTasks(false, false);

    const interval = setInterval(() => {
      getPrecacheTasks(false, false);
    }, 30000);

    return () => {
      clearInterval(interval);
      // Clean up precache controller reference
      if (getPrecacheTasksControllerRef.current) {
        getPrecacheTasksControllerRef.current.abort();
        getPrecacheTasksControllerRef.current = null;
      }
    };
  }, [getPrecacheTasks]);

  useLayoutEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    async function fetchData() {
      try {
        await Promise.all([
          initialGetAllAppUsers(),
          initialGetUserTasks({}),
          initialGetUserTags(),
          initialGetUserFavoriteSearches({}),
        ]);
      } catch (error) {
        if (signal.aborted) {
          console.log("Fetch aborted");
        } else {
          console.error("Fetch error:", error);
        }
      }
    }

    fetchData();

    return () => {
      controller.abort();
      // Clean up all controller references
      if (getAllAppUsersControllerRef.current) {
        getAllAppUsersControllerRef.current.abort();
        getAllAppUsersControllerRef.current = null;
      }
      if (getUserTasksControllerRef.current) {
        getUserTasksControllerRef.current.abort();
        getUserTasksControllerRef.current = null;
      }
      if (getUserTagsControllerRef.current) {
        getUserTagsControllerRef.current.abort();
        getUserTagsControllerRef.current = null;
      }
      if (getUserFavoriteSearchesControllerRef.current) {
        getUserFavoriteSearchesControllerRef.current.abort();
        getUserFavoriteSearchesControllerRef.current = null;
      }
    };
  }, [
    initialGetAllAppUsers,
    initialGetUserTasks,
    initialGetUserTags,
    initialGetUserFavoriteSearches,
  ]);

  useLayoutEffect(() => {
    if (!user) {
      return;
    }

    async function fetchData(user: ShortAppUser) {
      await Promise.all([initialGetUserTimers(user.id, false)]);
    }

    fetchData(user);

    return () => {
      // Clean up timer controller reference
      if (getUserTimersControllerRef.current) {
        getUserTimersControllerRef.current.abort();
        getUserTimersControllerRef.current = null;
      }
    };
  }, [user, initialGetUserTimers]);

  useLayoutEffect(() => {
    // find the first timer that is still running
    if (!userTimers || userTimers.length === 0) {
      return;
    }
    const runningTimer = userTimers.find((timer) => {
      if (timer.events.length === 0) {
        return false;
      }
      const lastEvent = timer.events[timer.events.length - 1];
      return lastEvent.end === undefined;
    });

    console.log("Running timer", runningTimer);

    setRunningTimer(runningTimer || null);
  }, [userTimers]);

  useEffect(() => {
    // Create a BroadcastChannel for localforage synchronization
    const channel = new BroadcastChannel("localforage_channel");

    // Listen for messages from other tabs
    channel.onmessage = async (message) => {
      console.log("Received message:", message.data);

      // Check if the message indicates that a lock was created
      if (message.data === "timerLockCreated") {
        const updatedLock: string | null =
          await localforage.getItem("timerLock");
        if (updatedLock) {
          setTimerLock(updatedLock);
        }
      }

      if (message.data === "timerLockRemoved") {
        const updatedLock: string | null =
          await localforage.getItem("timerLock");
        if (!updatedLock) {
          setTimerLock(null);

          const lsUserTimers = await locForGetUserTimers();
          if (lsUserTimers) {
            const deepCloneTimers = cloneDeep(lsUserTimers);
            const deepCloneUserTimers = cloneDeep(userTimers);
            if (!isEqual(deepCloneTimers, deepCloneUserTimers)) {
              setUserTimers(lsUserTimers);
            }
          }
        }
      }
    };

    // Cleanup the BroadcastChannel when the component unmounts
    return () => {
      channel.close();
    };
  }, [userTimers]);

  // TODO:  Check for lock every second
  useEffect(() => {
    if (!runningTimer) {
      const interval = setInterval(async () => {
        const lockState: string | null = await checkLock();

        if (lockState === "locked") {
          // find the first timer that is still running
          const lsUserTimers = await locForGetUserTimers();
          if (lsUserTimers) {
            const deepCloneTimers = cloneDeep(lsUserTimers);
            const deepCloneUserTimers = cloneDeep(userTimers);
            if (!isEqual(deepCloneTimers, deepCloneUserTimers)) {
              setUserTimers(lsUserTimers);
            }
          }

          if (!userTimers || userTimers.length === 0) {
            return;
          }

          const runningTimer = userTimers.find((timer) => {
            if (timer.events.length === 0) {
              return false;
            }
            const lastEvent = timer.events[timer.events.length - 1];
            return lastEvent.end === undefined;
          });

          setRunningTimer(runningTimer || null);
        }
      }, 1000);

      return () => clearInterval(interval); // Cleanup the interval on component unmount
    }
  }, [runningTimer, timerLock, userTimers]);

  // Refetch All Data
  const forceFetchAll = useCallback(async () => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      await Promise.all([
        initialGetAllAppUsers(true),
        initialGetUserTasks({ force: true }),
        initialGetUserTags(true),
        initialGetUserFavoriteSearches({ force: true }),
        getPrecacheTasks(false, true, false),
        initialGetUserTimers(user?.id || -1, true),
        fetchPageTimers(signal, false),
      ]);
    } catch (error) {
      if (signal.aborted) {
        console.log("Fetch aborted");
        return;
      } else {
        genericFailureToast("Failed to fetch precache");
        console.error("Fetch error:", error);
        return;
      }
    }
    genericSuccessToast("Cleared and fetched all!");
  }, [
    fetchPageTimers,
    getPrecacheTasks,
    initialGetAllAppUsers,
    initialGetUserFavoriteSearches,
    initialGetUserTags,
    initialGetUserTasks,
    initialGetUserTimers,
    user?.id,
  ]);

  function startTimer(taskID: number, taskName: string) {
    const now = new Date();
    const newTimedTask = { taskID, taskName, taskStartDate: now };
    // setTimedTask(newTimedTask);
    localStorage.setItem("timed-task", JSON.stringify(newTimedTask));
  }

  async function stopTimer() {
    console.log("Stopping timer");
    // add end time to the timed task
    const now = new Date();
    if (!runningTimer) {
      console.error("No running timer found");
      const remove = await removeLock();
      setTimerLock(remove);
      genericSuccessToast("No running timers found, timer lock removed");
      return;
    }

    const res = await sendStopTimer(runningTimer.id, now.toISOString());
    if (res.rStatus === "error") {
      console.error("Failed to stop timer");
      genericFailureToast("Failed to stop timer");
      return;
    }

    console.log("Timer stopped");
    if (!userTimers) {
      const remove = await removeLock();
      setTimerLock(remove);
      genericSuccessToast("No running timers found, timer lock removed");
      return;
    }

    // edit userTimers
    const newTimers: UserTimer[] = userTimers.map((t) =>
      t.id === runningTimer.id
        ? {
            ...t,
            events: t.events.map((event, index) =>
              index === t.events.length - 1 // Check if it's the last event
                ? {
                    ...event,
                    end: now.toISOString(), // Add the end time to the last event
                  }
                : event
            ),
          }
        : t
    );

    console.log("New timers", newTimers);

    setUserTimers(newTimers);
    locForSetUserTimers(newTimers);
    fetchPageTimers(controllerRef.current.signal, fetchEveryoneTimers);
    const remove = await removeLock();
    setTimerLock(remove);
  }

  async function stopLocalTimer(taskID: number) {
    console.log("Stopping local timer");
    // add end time to the timed task
    const now = new Date();
    if (!runningTimer) {
      return;
    }

    if (runningTimer.task_id !== taskID) {
      return;
    }

    const lastEvent = runningTimer.events[runningTimer.events.length - 1];
    const newEvent = { ...lastEvent, end: now.toISOString() };

    // runningTimer.events[runningTimer.events.length - 1] = newEvent;
    const newEventList = runningTimer.events.map((event, index) => {
      if (index === runningTimer.events.length - 1) {
        return newEvent;
      }
      return event;
    });

    const newTimedTask = { ...runningTimer, events: newEventList };

    console.log("New timed task", newTimedTask);

    console.log("Timer stopped");
    if (!userTimers) {
      return;
    }
    // edit userTimers
    const newTimers = userTimers.map((t) =>
      t.id === runningTimer.id ? newTimedTask : t
    );
    setUserTimers(newTimers);
    locForSetUserTimers(newTimers);
    fetchPageTimers(controllerRef.current.signal, fetchEveryoneTimers);
    const remove = await removeLock();
    setTimerLock(remove);
  }

  // useLayoutEffect(() => {
  //   console.log(runningTimer);
  // }, [runningTimer]);

  const resetTimer = () => {
    //   if (!timedTask) {
    //     return;
    //   }
    //   const now = new Date();
    //   const newTimedTask = { ...timedTask, taskStartDate: now };
    //   setTimedTask(newTimedTask);
    //   localStorage.setItem("timed-task", JSON.stringify(newTimedTask));
  };

  function updateAllTasks(tasks: BasicTask[], force?: boolean) {
    if (!isEqual(tasks, userTasks) || force) {
      locForSetUserTasks(tasks);
      setUserTasks(tasks);
    }
  }

  // Fetch full task
  const getFullTask = useCallback(
    async (taskParams: FetchTaskParams) => {
      const { taskID, signal, editing } = taskParams;
      console.log("Getting full task", taskID);
      if (!taskID) {
        setSingleTask({ data: null, loading: false, error: "Task not found" });
        return;
      }

      // If the active task ID doesn't match, skip the update
      if (activeTaskIdRef.current !== taskID) {
        console.log("Stale fetch detected. Skipping update for task:", taskID);
        return;
      }
      if (editing) {
        setSingleTask((prev) => ({
          ...prev,
          loading: true,
          error: null,
        }));
      } else {
        setSingleTask({ data: null, loading: true, error: null });
      }
      const response = await fetchTask({ taskID: taskID, signal: signal });

      // If the active task ID doesn't match, skip the update
      if (activeTaskIdRef.current !== taskID) {
        console.log("Stale fetch detected. Skipping update for task:", taskID);
        return;
      }

      if (response.rStatus === "error") {
        console.log(response.message);
        if (
          response.message === "request_aborted" ||
          (signal && signal.aborted)
        ) {
          console.log("Fetch aborted");
          return;
        }
        console.error(response.message);
        if (editing) {
          setSingleTask((prev) => ({
            ...prev,
            loading: true,
            error: null,
          }));
        } else {
          setSingleTask({
            data: null,
            loading: false,
            error: response.message,
          });
        }
        return;
      }
      setSingleTask({ data: response.task, loading: false, error: null });
    },
    [setSingleTask]
  );

  async function sendNewTimerStart({
    taskID,
    timerName,
    startTime,
    endTime,
  }: {
    taskID: number | null;
    timerName: string | null;
    startTime?: Date | null;
    endTime?: Date | null;
  }): Promise<boolean> {
    const lockState = await checkLock();
    if (lockState === "locked") {
      genericFailureToast(
        "Timer is locked, please end the current timer or sync the timer."
      );
      handleTimerLockedState();
      return false;
    }

    const now = new Date().toISOString();
    const response = await sendNewUserTimer({
      taskID,
      name: timerName || "",
      startTime: startTime ? startTime.toISOString() : now,
      endTime: endTime ? endTime.toISOString() : null,
    });

    if (response.rStatus === "error") {
      console.error("Failed to send new timer start");
      return false;
    }

    console.log("New timer start sent");
    // update local storage and userTimers
    const newTimer = response.rData;
    // add new timer to userTimers
    const newTimers = [...(userTimers ?? []), newTimer];
    setUserTimers(newTimers);
    locForSetUserTimers(newTimers);
    await initialGetUserTimers(user?.id || -1, true);
    const lock = await createLock();
    setTimerLock(lock);
    return true;
  }

  async function sendUpdateTimer(timer: UserTimer) {
    if (!userTimers) {
      console.error("User timers not found");
      return;
    }

    //   type UserTimer = {
    //     id: number;
    //     name: string;
    //     task_id: number | null;
    //     user_id: number;
    //     events: {
    //         position: number;
    //         start: string;
    //         end?: string | null | undefined;
    //     }[];
    // }

    //   const OrganizedTimersEvent: {
    //     timer: {
    //         id: number;
    //         name: string;
    //         task_id: number | null;
    //         user_id: number;
    //         task_name: string | null;
    //         tags: number[] | null;
    //         totalTime: number | null;
    //     };
    //     position: number;
    //     event_start: string;
    //     event_end: string | null | undefined;
    // }

    const task = userTasks?.find((t) => t.id === timer.task_id);

    const convertedTimer: OrganizedTimersEvent = {
      timer: {
        id: timer.id,
        name: timer.name,
        task_id: timer.task_id,
        user_id: timer.user_id,
        task_name: task?.title || null,
        tags: task?.tags.map((tag) => tag.id) || null,
        totalTime: 1,
      },
      position: timer.events.length,
      event_start: timer.events[timer.events.length - 1].start,
      event_end: timer.events[timer.events.length - 1].end,
    };

    const response = await updateTimer({ timer: convertedTimer });

    if (response.rStatus === "error") {
      console.error("Failed to send update timer");
      return;
    }

    console.log("Timer updated");
    // update local storage
    const newTimers = userTimers.map((t) => (t.id === timer.id ? timer : t));
    setUserTimers(newTimers);
    locForSetUserTimers(newTimers);
  }

  function hasProjectManager(collaborators: number[] | undefined): boolean {
    if (!collaborators) {
      return false;
    }

    return collaborators.some((collab) => {
      const user = allAppUsers?.find((u) => u.id === collab);
      return (
        user?.role_name === "project_manager" ||
        user?.role_name === "super_admin"
      );
    });
  }

  // continueTimer: (timer: { id: number; startTime: string }) => void;

  async function continueTimer(timer: { id: number; startTime: string }) {
    const lockState = await checkLock();
    if (lockState === "locked") {
      genericFailureToast(
        "Timer is locked, please end the current timer or sync the timer."
      );
      handleTimerLockedState();
      return;
    }

    const res = await sendStartTimer(timer.id, timer.startTime);
    console.log("res", res);
    if (res.rStatus === "error") {
      console.error("Failed to continue timer");
      return;
    }

    console.log("Timer continued");

    if (!userTimers) {
      console.error("User timers not found");
      return;
    }

    // update local storage
    const newTimers = userTimers.map((t) =>
      t.id === timer.id
        ? {
            ...t,
            events: [
              ...t.events,
              { start: timer.startTime, position: t.events.length },
            ],
          }
        : t
    );

    setUserTimers(newTimers);
    locForSetUserTimers(newTimers);
    const lock = await createLock();
    setTimerLock(lock);
  }

  const handleTimerLockedState = () => {
    setIsLockedAnimation(true);
    setTimeout(() => setIsLockedAnimation(false), 5000); // Reset after 5 seconds
  };

  async function updateFavoriteSearches(searches: FavoriteSearch[]) {
    // store old searches in case of error
    const oldFavorites = favoriteSearches;

    setFavoriteSearches(searches);

    // clear local storage for favorite searches incase of refresh before update
    locForClearFavoriteSearches();

    const response = await sendUpdatedSearchFavorites({ searches: searches });
    if (response.rStatus === "error") {
      console.error(`Failed to update favorite searches: ${response.message}`);
      setFavoriteSearches(oldFavorites);
      locForSetFavoriteSearches(oldFavorites);
      genericFailureToast(
        `Failed to update favorite searches: ${response.message}`
      );
      return;
    }
    // update local storage
    locForSetFavoriteSearches(searches);
    genericSuccessToast("Favorite searches updated");
  }

  return error ? (
    <div className="text-2xl text-center mt-5 text-primary">
      <h1>Could not load SuperCharged</h1>
      <br />
      <p>{error}</p>
    </div>
  ) : !user || !allAppUsers || !userTasks || !userTags || !userTimers ? (
    <div className="d-flex justify-content-center align-items-center flex-column text-2xl text-center mt-5">
      <CustomLoader />
      <div className="mt-3 font-bold text-primary">Fetching Your Data...</div>
    </div>
  ) : !user ? (
    <div className="d-flex justify-content-center align-items-center flex-column text-2xl text-center mt-5">
      <CustomLoader />
      <div className="mt-3 font-bold text-primary">Building App...</div>
    </div>
  ) : (
    <AppContext.Provider
      value={{
        pageLoading,
        user,
        allAppUsers,
        userTasks,
        userTags,
        userTimers,
        runningTimer,
        precacheTasks,
        collaborators,
        favoriteSearches,
        pageTimers,
        fetchEveryoneTimers,
        singleTask,
        activeTaskIdRef,
        isLockedAnimation,
        forceFetchAll,
        toggleFetchAll,
        fetchPageTimers,
        startTimer,
        stopTimer,
        resetTimer,
        updateAllTasks,
        sendNewTimerStart,
        removeTaskFromUserTasks,
        sendUpdateTimer,
        hasProjectManager,
        initialGetUserTasks,
        getPrecacheTasks,
        initialGetUserFavoriteSearches,
        stopLocalTimer,
        initialGetUserTimers,
        continueTimer,
        updateFavoriteSearches,
        getFullTask,
        initialGetAllAppUsers,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
